import React from 'react'
import { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import { delFetcher, postFetcher, putFetcher } from 'api/swr'
import {
  DotsHorizontalIcon,
  TrashIcon,
  UpdateIcon,
} from '@radix-ui/react-icons'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { TAG_I18N_LABELS, TAG_I18N_TARGET_LANGS } from 'commons/tagI18n'
const LEVEL_OPTIONS = [
  { code: 2, name: 'Level 2' },
  { code: 3, name: 'Level 3' },
]

const refetchTaglibs = () =>
  mutate((key) => Array.isArray(key) && key[0] === '/taglibs')

export const DataTableRowActions = ({ row }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DataTableRowEditI18n row={row} />
        <DataTableRowSetLevel row={row} />
        <DataTableRowDelete row={row} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const DataTableRowEditI18n = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState({})
  const { trigger: saveLang, isMutating: saving } = useSWRMutation(
    `/taglibs/${data.id}/i18n`,
    putFetcher,
  )
  const { trigger: retranslate, isMutating: translating } = useSWRMutation(
    `/taglibs/${data.id}/i18n/translate`,
    postFetcher,
  )

  const openDialog = (e) => {
    e.preventDefault()
    const i18n = data.i18n || {}
    setDraft(
      Object.fromEntries(
        TAG_I18N_TARGET_LANGS.map((lang) => [lang, i18n[lang] || '']),
      ),
    )
    setOpen(true)
  }

  const handleSave = async () => {
    try {
      for (const lang of TAG_I18N_TARGET_LANGS) {
        const value = String(draft[lang] || '').trim()
        const previous = String(data.i18n?.[lang] || '').trim()
        if (!value || value === previous) continue
        await saveLang({ lang, value })
      }
      toast({ title: 'Translations saved' })
      setOpen(false)
      refetchTaglibs()
    } catch (error) {
      toast({
        title: 'Save failed',
        description: error.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const handleRetranslate = async () => {
    try {
      await retranslate({ force: true })
      toast({ title: 'Translate queued' })
      setOpen(false)
      refetchTaglibs()
    } catch (error) {
      toast({
        title: 'Translate failed',
        description: error.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenuItem onSelect={openDialog}>Translations</DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Translations · {data.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {TAG_I18N_LABELS.en}: {data.name}
          </p>
          <div className="space-y-3">
            {TAG_I18N_TARGET_LANGS.map((lang) => (
              <label key={lang} className="block space-y-1">
                <span className="text-sm font-medium">
                  {TAG_I18N_LABELS[lang]}
                </span>
                <Input
                  value={draft[lang] || ''}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, [lang]: e.target.value }))
                  }
                />
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleRetranslate}
              disabled={saving || translating}
            >
              {translating ? 'Queueing...' : 'Retranslate'}
            </Button>
            <Button onClick={handleSave} disabled={saving || translating}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const DataTableRowSetLevel = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation(
    `/taglibs/${data.id}`,
    putFetcher,
  )
  const [open, setOpen] = React.useState(false)
  const [pendingLevel, setPendingLevel] = React.useState(null)

  const handleSelectLevel = (e, level) => {
    e.preventDefault()
    if (Number(level) === Number(data.level)) return
    setPendingLevel(level)
    setOpen(true)
  }

  const handleConfirm = async (e) => {
    e.preventDefault()
    try {
      await trigger({ key: 'level', value: pendingLevel })
      toast({
        title: 'Success',
        description: `Set level to ${pendingLevel} successfully`,
        className: 'bg-green-500 text-white',
      })
      setOpen(false)
      refetchTaglibs()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Set level failed: ' + (error.message || 'Unknown Error'),
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          Set Level{isMutating ? '...' : ''}
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            {LEVEL_OPTIONS.map((level) => (
              <DropdownMenuCheckboxItem
                key={level.code}
                checked={Number(level.code) === Number(data.level)}
                onSelect={(e) => handleSelectLevel(e, level.code)}
              >
                {level.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm to set Level</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Change tag <span className="font-medium">{data.name}</span> from
            level {data.level} to level {pendingLevel}?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

const DataTableRowDelete = ({ row }) => {
  const data = row.original
  const { toast } = useToast()
  const { trigger, isMutating } = useSWRMutation('/taglibs', delFetcher, {
    onSuccess: () => {
      refetchTaglibs()
      toast({
        title: 'Tag Deleted',
        description: 'The tag has been successfully deleted.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Delete failed',
        variant: 'destructive',
      })
    },
  })
  const [open, setOpen] = React.useState(false)

  const openDialog = (e) => {
    e.preventDefault()
    setOpen(true)
  }

  const handleDelete = async () => {
    await trigger({ id: data.id })
    setOpen(false)
  }

  return (
    <>
      <DropdownMenuItem
        className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
        onSelect={openDialog}
      >
        {isMutating ? (
          <>
            <UpdateIcon className="mr-2 h-4 w-4" />
            <span>Deleting...</span>
          </>
        ) : (
          <>
            <TrashIcon className="mr-2 h-4 w-4" />
            <span>Delete</span>
          </>
        )}
      </DropdownMenuItem>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Tag</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete tag{' '}
            <span className="font-medium">{data.name}</span>
            {(data.frameCount > 0 || data.dlcCount > 0) && (
              <>
                ? It is used by {data.frameCount ?? 0} frames and{' '}
                {data.dlcCount ?? 0} dlcs, which will be unbound.
              </>
            )}
            {!(data.frameCount > 0 || data.dlcCount > 0) && '?'}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isMutating}
            >
              {isMutating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
